import { ethers } from 'ethers'
import usdcABI from './usdcABI.json'
import usdcEscrowABI from './usdcEscrowABI.json'

// Type assertion for JSON imports
const usdcABITyped = usdcABI as any
const usdcEscrowABITyped = usdcEscrowABI as any

const USDC_CONTRACT_ADDRESS = import.meta.env.VITE_USDC_CONTRACT_ADDRESS
const USDC_ESCROW_CONTRACT_ADDRESS = import.meta.env.VITE_USDC_ESCROW_CONTRACT_ADDRESS
const BASE_RPC_URL = import.meta.env.VITE_BASE_RPC_URL
const BASE_CHAIN_ID = 8453

// USDC has 6 decimals (not 18 like most ERC20 tokens)
const USDC_DECIMALS = 6

export interface USDCAllowanceInfo {
  hasEnoughAllowance: boolean
  currentAllowance: string
  requiredAmount: string
}

export interface USDCBalanceInfo {
  hasEnoughBalance: boolean
  currentBalance: string
  requiredAmount: string
  deficit: string
}

export interface DepositResult {
  transactionHash: string
  blockNumber: number
  status: 'success' | 'failed'
}

/**
 * Check if user has enough USDC balance for tournament creation
 * @param provider - Ethereum provider from wallet
 * @param walletAddress - User's wallet address
 * @param requiredAmount - Required USDC amount (in USDC, not smallest unit)
 * @returns Balance information
 */
export const checkUSDCBalance = async (
  provider: any,
  walletAddress: string,
  requiredAmount: number
): Promise<USDCBalanceInfo> => {
  try {
    if (!USDC_CONTRACT_ADDRESS) {
      throw new Error('USDC contract address not configured')
    }

    const ethersProvider = new ethers.BrowserProvider(provider)
    const usdcContract = new ethers.Contract(
      USDC_CONTRACT_ADDRESS,
      usdcABI,
      ethersProvider
    )

    // Ensure wallet address is properly formatted (prevents ENS resolution)
    const checksummedWalletAddress = ethers.getAddress(walletAddress)

    // Get current balance
    const balanceInSmallestUnit = await usdcContract.balanceOf(checksummedWalletAddress)
    const currentBalance = ethers.formatUnits(balanceInSmallestUnit, USDC_DECIMALS)
    const currentBalanceNum = parseFloat(currentBalance)

    const hasEnoughBalance = currentBalanceNum >= requiredAmount
    const deficit = hasEnoughBalance ? '0' : (requiredAmount - currentBalanceNum).toFixed(6)

    return {
      hasEnoughBalance,
      currentBalance,
      requiredAmount: requiredAmount.toFixed(6),
      deficit,
    }
  } catch (error) {
    console.error('Error checking USDC balance:', error)
    throw error
  }
}

/**
 * Check if user has approved enough USDC allowance for escrow contract
 * @param provider - Ethereum provider from wallet
 * @param walletAddress - User's wallet address
 * @param requiredAmount - Required USDC amount (in USDC, not smallest unit)
 * @returns Allowance information
 */
export const checkUSDCAllowance = async (
  provider: any,
  walletAddress: string,
  requiredAmount: number
): Promise<USDCAllowanceInfo> => {
  try {
    if (!USDC_CONTRACT_ADDRESS || !USDC_ESCROW_CONTRACT_ADDRESS) {
      throw new Error('Contract addresses not configured')
    }

    const ethersProvider = new ethers.BrowserProvider(provider)
    const usdcContract = new ethers.Contract(
      USDC_CONTRACT_ADDRESS,
      usdcABI,
      ethersProvider
    )

    // Ensure wallet address is properly formatted (prevents ENS resolution)
    const checksummedWalletAddress = ethers.getAddress(walletAddress)
    const checksummedEscrowAddress = ethers.getAddress(USDC_ESCROW_CONTRACT_ADDRESS)

    // Get current allowance for escrow contract
    const allowanceInSmallestUnit = await usdcContract.allowance(
      checksummedWalletAddress,
      checksummedEscrowAddress
    )
    const currentAllowance = ethers.formatUnits(allowanceInSmallestUnit, USDC_DECIMALS)
    const currentAllowanceNum = parseFloat(currentAllowance)

    const hasEnoughAllowance = currentAllowanceNum >= requiredAmount

    return {
      hasEnoughAllowance,
      currentAllowance,
      requiredAmount: requiredAmount.toFixed(6),
    }
  } catch (error) {
    console.error('Error checking USDC allowance:', error)
    throw error
  }
}

/**
 * Approve USDC spending for escrow contract
 * @param provider - Ethereum provider from Privy wallet
 * @param amount - Amount to approve (in USDC, not smallest unit)
 * @param walletAddress - User's wallet address
 * @returns Transaction hash
 */
export const approveUSDCForEscrow = async (
  provider: any,
  amount: number,
  walletAddress: string
): Promise<any> => {
  try {
    if (!USDC_CONTRACT_ADDRESS || !USDC_ESCROW_CONTRACT_ADDRESS) {
      throw new Error('Contract addresses not configured')
    }

    // Convert amount to smallest unit (6 decimals for USDC)
    const amountInSmallestUnit = ethers.parseUnits(amount.toFixed(6), USDC_DECIMALS)

    // Debug logging - only in development
    if (import.meta.env.DEV) {
      console.log('Approving USDC for escrow')
    }

    // Create USDC contract interface for encoding
    const usdcInterface = new ethers.Interface(usdcABI)

    // Encode the approve function call
    const data = usdcInterface.encodeFunctionData('approve', [
      USDC_ESCROW_CONTRACT_ADDRESS,
      amountInSmallestUnit
    ])

    // Estimate gas for the transaction
    const ethersProvider = new ethers.BrowserProvider(provider)
    const gasEstimate = await ethersProvider.estimateGas({
      from: walletAddress,
      to: USDC_CONTRACT_ADDRESS,
      data: data,
    })

    // Add 20% buffer to gas estimate
    const gasLimit = (gasEstimate * BigInt(120)) / BigInt(100)

    // Use Privy's provider.request method (required for embedded wallet)
    const transactionRequest = {
      from: walletAddress,
      to: USDC_CONTRACT_ADDRESS,
      data: data,
      gas: `0x${gasLimit.toString(16)}`,
    }

    // This will trigger Privy wallet popup
    const txHash = await provider.request({
      method: 'eth_sendTransaction',
      params: [transactionRequest],
    })

    // Wait for confirmation
    const receipt = await ethersProvider.waitForTransaction(txHash)

    return receipt
  } catch (error) {
    console.error('Error approving USDC for escrow:', error)
    throw error
  }
}

/**
 * Deposit USDC into escrow contract for tournament
 * @param provider - Ethereum provider from Privy wallet
 * @param tournamentId - Tournament ID (MongoDB ObjectId as string)
 * @param amount - Amount to deposit (in USDC, not smallest unit)
 * @param walletAddress - User's wallet address
 * @returns Deposit result with transaction hash
 */
export const depositUSDCToEscrow = async (
  provider: any,
  tournamentId: string,
  amount: number,
  walletAddress: string
): Promise<DepositResult> => {
  try {
    if (!USDC_ESCROW_CONTRACT_ADDRESS) {
      throw new Error('USDC Escrow contract address not configured')
    }

    // Convert amount to smallest unit (6 decimals for USDC)
    const amountInSmallestUnit = ethers.parseUnits(amount.toFixed(6), USDC_DECIMALS)

    // Debug logging - only in development
    if (import.meta.env.DEV) {
      console.log('Depositing USDC to escrow')
    }

    // Create escrow contract interface for encoding
    const escrowInterface = new ethers.Interface(usdcEscrowABI)

    // Encode the depositTournament function call
    const data = escrowInterface.encodeFunctionData('depositTournament', [
      tournamentId,
      amountInSmallestUnit
    ])

    // Estimate gas for the transaction with fallback
    const ethersProvider = new ethers.BrowserProvider(provider)
    let gasLimit: bigint
    try {
      const gasEstimate = await ethersProvider.estimateGas({
        from: walletAddress,
        to: USDC_ESCROW_CONTRACT_ADDRESS,
        data: data,
      })
      // Add 50% buffer to gas estimate for safety
      gasLimit = (gasEstimate * BigInt(150)) / BigInt(100)
    } catch (gasError) {
      // Fallback to a safe default gas limit for escrow deposits
      if (import.meta.env.DEV) {
        console.warn('Gas estimation failed, using fallback')
      }
      gasLimit = BigInt(150000) // Safe fallback for escrow deposit
    }

    // Use Privy's provider.request method (required for embedded wallet)
    const transactionRequest = {
      from: walletAddress,
      to: USDC_ESCROW_CONTRACT_ADDRESS,
      data: data,
      gas: `0x${gasLimit.toString(16)}`,
    }

    // This will trigger Privy wallet popup
    const txHash = await provider.request({
      method: 'eth_sendTransaction',
      params: [transactionRequest],
    })

    // Wait for confirmation
    const receipt = await ethersProvider.waitForTransaction(txHash)

    return {
      transactionHash: receipt?.hash || txHash,
      blockNumber: receipt?.blockNumber || 0,
      status: receipt?.status === 1 ? 'success' : 'failed',
    }
  } catch (error) {
    console.error('Error depositing USDC to escrow:', error)
    throw error
  }
}

/**
 * Get tournament balance from escrow contract
 * @param tournamentId - Tournament ID
 * @returns Tournament balance in USDC
 */
export const getTournamentEscrowBalance = async (
  tournamentId: string
): Promise<string> => {
  try {
    if (!USDC_ESCROW_CONTRACT_ADDRESS || !BASE_RPC_URL) {
      throw new Error('Contract configuration missing')
    }

    const provider = new ethers.JsonRpcProvider(BASE_RPC_URL)
    const escrowContract = new ethers.Contract(
      USDC_ESCROW_CONTRACT_ADDRESS,
      usdcEscrowABI,
      provider
    )

    const balanceInSmallestUnit = await escrowContract.getTournamentBalance(tournamentId)
    const balance = ethers.formatUnits(balanceInSmallestUnit, USDC_DECIMALS)

    return balance
  } catch (error) {
    console.error('Error getting tournament escrow balance:', error)
    throw error
  }
}

/**
 * Switch to Base network if not already on it
 * @param provider - Ethereum provider from wallet
 * @returns True if on Base or successfully switched
 */
export const ensureBaseNetwork = async (provider: any): Promise<boolean> => {
  try {
    const ethersProvider = new ethers.BrowserProvider(provider)
    const network = await ethersProvider.getNetwork()
    const currentChainId = Number(network.chainId)

    if (currentChainId === BASE_CHAIN_ID) {
      return true
    }

    // Request network switch
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: `0x${BASE_CHAIN_ID.toString(16)}` }],
    })

    return true
  } catch (error: any) {
    // Chain not added to wallet
    if (error.code === 4902) {
      try {
        await provider.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: `0x${BASE_CHAIN_ID.toString(16)}`,
              chainName: 'Base',
              nativeCurrency: {
                name: 'Ethereum',
                symbol: 'ETH',
                decimals: 18,
              },
              rpcUrls: [BASE_RPC_URL],
              blockExplorerUrls: ['https://basescan.org'],
            },
          ],
        })

        return true
      } catch (addError) {
        throw new Error('Failed to add Base network to wallet')
      }
    }

    throw new Error('Failed to switch to Base network')
  }
}

/**
 * Complete USDC tournament creation flow
 * Checks balance, approves if needed, and deposits to escrow
 * @param provider - Ethereum provider from wallet
 * @param walletAddress - User's wallet address
 * @param tournamentId - Tournament ID
 * @param amount - Amount to deposit (in USDC)
 * @param onProgress - Callback for progress updates
 * @returns Deposit result
 */
export const executeUSDCTournamentDeposit = async (
  provider: any,
  walletAddress: string,
  tournamentId: string,
  amount: number,
  onProgress?: (step: string, message: string) => void
): Promise<DepositResult> => {
  try {
    // Step 1: Ensure on Base network
    onProgress?.('network', 'Ensuring Base network...')
    await ensureBaseNetwork(provider)

    // Step 2: Check balance
    onProgress?.('balance', 'Checking USDC balance...')
    const balanceInfo = await checkUSDCBalance(provider, walletAddress, amount)

    if (!balanceInfo.hasEnoughBalance) {
      throw new Error(
        `Insufficient USDC balance. You have ${balanceInfo.currentBalance} USDC but need ${balanceInfo.requiredAmount} USDC. Deficit: ${balanceInfo.deficit} USDC`
      )
    }

    // Step 3: Check allowance
    onProgress?.('allowance', 'Checking USDC allowance...')
    const allowanceInfo = await checkUSDCAllowance(provider, walletAddress, amount)

    // Step 4: Approve if needed
    if (!allowanceInfo.hasEnoughAllowance) {
      onProgress?.('approve', 'Requesting approval for USDC spending...')
      await approveUSDCForEscrow(provider, amount, walletAddress)
      onProgress?.('approve', 'USDC spending approved successfully')
    } else {
      onProgress?.('approve', 'USDC allowance already sufficient')
    }

    // Step 5: Deposit to escrow
    onProgress?.('deposit', 'Depositing USDC to tournament escrow...')
    const depositResult = await depositUSDCToEscrow(provider, tournamentId, amount, walletAddress)
    onProgress?.('deposit', 'USDC deposited successfully!')

    return depositResult
  } catch (error) {
    // Only log errors in development
    if (import.meta.env.DEV) {
      console.error('Error executing USDC tournament deposit:', error)
    }
    throw error
  }
}

/**
 * Tip/donate USDC to a tournament's prize pool
 * Anyone can tip to increase the prize pool for a funded tournament
 * @param provider - Ethereum provider from wallet
 * @param walletAddress - User's wallet address
 * @param tournamentId - Tournament ID
 * @param amount - Amount to tip (in USDC)
 * @param onProgress - Callback for progress updates
 * @returns Deposit result
 */
export const tipUSDCToTournament = async (
  provider: any,
  walletAddress: string,
  tournamentId: string,
  amount: number,
  onProgress?: (step: string, message: string) => void
): Promise<DepositResult> => {
  try {
    // Step 1: Ensure on Base network
    onProgress?.('network', 'Ensuring Base network...')
    await ensureBaseNetwork(provider)

    // Step 2: Check balance
    onProgress?.('balance', 'Checking USDC balance...')
    const balanceInfo = await checkUSDCBalance(provider, walletAddress, amount)

    if (!balanceInfo.hasEnoughBalance) {
      throw new Error(
        `Insufficient USDC balance. You have ${balanceInfo.currentBalance} USDC but need ${amount} USDC.`
      )
    }

    // Step 3: Check allowance
    onProgress?.('allowance', 'Checking USDC allowance...')
    const allowanceInfo = await checkUSDCAllowance(provider, walletAddress, amount)

    // Step 4: Approve if needed
    if (!allowanceInfo.hasEnoughAllowance) {
      onProgress?.('approve', 'Requesting approval for USDC spending...')
      await approveUSDCForEscrow(provider, amount, walletAddress)
      onProgress?.('approve', 'USDC spending approved successfully')
    }

    // Step 5: Tip to escrow using tipTournament function
    onProgress?.('tip', 'Tipping USDC to tournament...')

    if (!USDC_ESCROW_CONTRACT_ADDRESS) {
      throw new Error('USDC Escrow contract address not configured')
    }

    // Convert amount to smallest unit (6 decimals for USDC)
    const amountInSmallestUnit = ethers.parseUnits(amount.toFixed(6), USDC_DECIMALS)

    // Create escrow contract interface for encoding
    const escrowInterface = new ethers.Interface(usdcEscrowABI)

    // Encode the tipTournament function call
    const data = escrowInterface.encodeFunctionData('tipTournament', [
      tournamentId,
      amountInSmallestUnit
    ])

    // Estimate gas for the transaction
    const ethersProvider = new ethers.BrowserProvider(provider)
    const gasEstimate = await ethersProvider.estimateGas({
      from: walletAddress,
      to: USDC_ESCROW_CONTRACT_ADDRESS,
      data: data,
    })

    // Add 20% buffer to gas estimate
    const gasLimit = (gasEstimate * BigInt(120)) / BigInt(100)

    // Use Privy's provider.request method
    const transactionRequest = {
      from: walletAddress,
      to: USDC_ESCROW_CONTRACT_ADDRESS,
      data: data,
      gas: `0x${gasLimit.toString(16)}`,
    }

    const txHash = await provider.request({
      method: 'eth_sendTransaction',
      params: [transactionRequest],
    })

    // Wait for confirmation
    const receipt = await ethersProvider.waitForTransaction(txHash)

    onProgress?.('tip', 'Tip successful!')

    return {
      transactionHash: receipt?.hash || txHash,
      blockNumber: receipt?.blockNumber || 0,
      status: receipt?.status === 1 ? 'success' : 'failed',
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error tipping USDC to tournament:', error)
    }
    throw error
  }
}
